import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import { defaultTheme } from '@styles/theme.styled';
import Wrapper from '@components/wrapper.styled';
import generateId from '@helpers/generate-id';
import Text from '@components/text';
import AnimatedElement from '@components/animated-element';
import {
  StyledSection,
  StyledImage,
  StyledGrid,
  StyledBlock,
  StyledIcon,
  StyledTextContent,
  Container,
  StyledIconImage,
  StyledDescription,
  TextContent,
} from './master-dispatch.styled';

interface MasterDispatchItemType {
  icon?: ImageProps;
  title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
}

export type BackgroundAlignment = 'hidden' | 'left' | 'right' | 'center';

export interface MasterDispatchProps {
  image?: ImageProps;
  title?: string;
  description?: RichTextBlock[];
  items?: MasterDispatchItemType[];
  backgroundColor?: keyof typeof defaultTheme.backgroundColors;
  enclosureColor?: keyof typeof defaultTheme.backgroundColors;
  showOverflow?: boolean;
  columns?: number;
  orientation?: boolean;
}

const MasterDispatch: React.FC<MasterDispatchProps> = ({
  image,
  title,
  description,
  items = [],
  backgroundColor = 'white',
  enclosureColor,
  columns,
  orientation,
  showOverflow = true,
}) => {
  const idGenerator = useMemo(() => generateId(), []);

  const hasItems = useMemo(() => {
    if (!items.length) return false;
    const firstItem = items[0];
    return (
      firstItem?.icon?.url !== null ||
      (firstItem?.description?.richText?.length ?? 0) > 0 ||
      firstItem?.title?.text !== ''
    );
  }, [items]);

  const renderGrid = useMemo(() => {
    if (!items.length) return null;

    return (
      <StyledGrid columns={columns} role="list" aria-label="Feature list">
        {items.map((item, index) => (
          <AnimatedElement
            key={idGenerator.next().value}
            animationType="slideUp"
            delay={index * 0.125}
            threshold={0.1}
            rootMargin="50px"
          >
            <StyledBlock role="listitem">
              {!!(item.icon?.gatsbyImageData || item.icon?.url) && (
                <StyledIcon>
                  <StyledIconImage
                    gatsbyImageData={item.icon?.gatsbyImageData}
                    url={item.icon?.url}
                    alt={item.icon?.alt || item.title?.text || 'Feature icon'}
                    objectFit="contain"
                  />
                </StyledIcon>
              )}

              <TextContent>
                {!!item.title?.text && (
                  <Text
                    type="title"
                    tag="h3"
                    stringText={item.title.text}
                    fontSize="heading6"
                    fontColor={enclosureColor ? 'dark' : 'white'}
                    margin="none"
                  />
                )}
                {!!item.description?.richText && item.description.richText.length > 0 && (
                  <StyledDescription
                    type="description"
                    richText={item.description.richText}
                    fontSize="body"
                    fontColor={enclosureColor ? 'dark' : 'light'}
                  />
                )}
              </TextContent>
            </StyledBlock>
          </AnimatedElement>
        ))}
      </StyledGrid>
    );
  }, [items, idGenerator]);

  return (
    <StyledSection backgroundColor={backgroundColor} showOverflow={showOverflow}>
      <Wrapper>
        <Container enclosureColor={enclosureColor} orientation={orientation}>
          <StyledTextContent>
            {!!title && (
              <Text
                type="title"
                tag="h2"
                stringText={title}
                fontSize="heading2"
                fontColor={enclosureColor ? 'secondary' : 'white'}
                margin="none"
              />
            )}
            {!!description && description.length > 0 && description[0]?.text !== '' && (
              <Text
                type="subheading"
                richText={description}
                fontColor={enclosureColor ? 'dark' : 'white'}
                margin="none"
              />
            )}
          </StyledTextContent>
          {!!(image?.gatsbyImageData || image?.url) && (
            <StyledImage
              gatsbyImageData={image?.gatsbyImageData}
              url={image?.url}
              alt={image?.alt || title || 'Decorative image'}
            />
          )}
          {hasItems && renderGrid}
        </Container>
      </Wrapper>
    </StyledSection>
  );
};

export default React.memo(MasterDispatch);
